import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase, getCurrentUser } from '../services/supabase';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUser = async () => {
      setLoading(true);
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      // If we have a session, check if it needs refresh
      if (currentSession) {
        const expiresAt = currentSession.expires_at * 1000; // Convert to milliseconds
        const now = Date.now();
        
        // If token expires in less than 5 minutes, refresh it
        if (expiresAt - now < 5 * 60 * 1000) {
          const { data: { session: newSession } } = await supabase.auth.refreshSession();
          if (newSession?.provider_token) {
            localStorage.setItem('googleAccessToken', newSession.provider_token);
          }
          setSession(newSession);
          setUser(newSession?.user || null);
        } else {
          setSession(currentSession);
          setUser(currentSession.user);
        }
      } else {
        setSession(null);
        setUser(null);
      }
      
      setLoading(false);
    };

    // Initial check
    checkUser();

    // Set up auth state listener
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          setUser(session.user);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
        }
      }
    );

    // Cleanup
    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  const value = {
    user,
    session,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 